/**
 * audio.ts — combat sound, synthesized rather than sampled.
 *
 * Every sound here is built from oscillators and noise at the moment it plays.
 * That is a deliberate consequence of the asset rule (AGENTS.md stack
 * only) — no audio pipeline, no files to load, nothing to 404 on a fresh deploy —
 * and it buys something real besides: a synthesized hit can be scaled by contact
 * velocity along more than one axis at once. A hard hit is not just a louder
 * quiet hit; it is brighter and it rings longer, because those are separate
 * parameters rather than one gain on a fixed recording. That is what §Combat 6
 * means by "impact sounds scaled by contact velocity".
 *
 * Nothing in here reads game state or decides anything. It is told what happened
 * and where, and it makes a noise. Sounds are triggered from two places:
 *
 * - **Contacts** (hit / block / parry) from the server's combat events, which is
 *   the only authority on whether steel met anything.
 * - **Swings and guards** from what the renderer is actually drawing, so the local
 *   player's whoosh lands on the frame they clicked rather than a round trip later
 *   — the predicted pose is what fires it (AGENTS.md: the local player is predicted).
 *
 * Browsers refuse to start audio without a gesture, so `unlock` is wired to the
 * same click that takes pointer lock.
 */

import { FEEL } from '@dueler/shared';

/** A sound's position relative to the listener: which ear, and how far. */
export interface Placement {
  /** -1 hard left, +1 hard right. */
  pan: number;
  /** 0..1 attenuation already applied for distance. */
  gain: number;
}

/**
 * Placement for your own body: dead centre, full volume, no camera involvement.
 *
 * Your own sword is not a thing happening somewhere near you, it is a thing you
 * are doing, and the camera is a third-person convenience rather than where your
 * ears are. Placing it in the world instead means turning the camera mid-swing
 * pans and attenuates your own blade, which sounds wrong for the simple reason
 * that it is wrong.
 */
export const OWN_BODY: Placement = { pan: 0, gain: 1 };

export interface Audio {
  /** Starts the audio clock. Safe to call repeatedly; only the first does work. */
  unlock(): void;
  /**
   * Master volume, 0..1, applied immediately.
   *
   * Client-local and NOT in `FEEL`. That table is server-authoritative world state
   * broadcast to the whole room by the tuning panel, so a volume slider living
   * there would let any one player set everyone else's. How loud your speakers are
   * is nobody else's business.
   */
  setVolume(volume: number): void;
  readonly volume: number;
  /** A blade cutting air. `committed` swings are the loud ones. */
  swing(clip: 'slash' | 'stab', at: Placement): void;
  /** Steel into flesh. `speed` is contact speed in m/s. */
  hit(speed: number, at: Placement): void;
  /** Steel onto a held guard. */
  block(speed: number, at: Placement): void;
  /** Steel onto a guard inside the parry window: the reward sound. */
  parry(speed: number, at: Placement): void;
  /** The guard coming up or going down. Quiet; it is a texture, not an event. */
  guard(up: boolean, at: Placement): void;
  setMuted(muted: boolean): void;
  readonly muted: boolean;
  dispose(): void;
}

/**
 * Default master volume. There is no persistence for this — the client uses no
 * browser storage (AGENTS.md) — so it is a per-session setting, with
 * `?volume=` to start somewhere else and M to mute.
 */
const DEFAULT_VOLUME = 0.5;

/** Per-sound levels, relative to the master gain. Balanced by ear, not by theory. */
const LEVEL = {
  swing: 0.35,
  hit: 0.9,
  block: 0.8,
  parry: 0.85,
  guard: 0.16,
} as const;

/**
 * Distance at which a sound is at half volume, m. Generous relative to the arena:
 * a duel is close quarters and a swing across the room still matters.
 */
const HALF_VOLUME_M = 9;

/**
 * Maps a contact speed to 0..1. Everything about an impact scales off this —
 * loudness, brightness and ring length — so a graze and a committed blow are
 * audibly different events rather than the same one at two volumes.
 */
function power(speed: number): number {
  return Math.min(1, Math.max(0.15, speed / (FEEL.V_REF * 2)));
}

/**
 * Attenuation and stereo placement for a world-space point, given the listener's
 * position and its right-hand direction.
 *
 * A plain stereo pan rather than a PannerNode and a full listener: it needs no
 * per-frame bookkeeping, it cannot drift out of sync with the camera, and for
 * "which side is that swing coming from" it is indistinguishable. The one thing
 * that matters in a duel is the sign.
 */
export function place(
  listener: { x: number; y: number; z: number },
  right: { x: number; y: number; z: number },
  at: { x: number; y: number; z: number },
): Placement {
  const dx = at.x - listener.x;
  const dy = at.y - listener.y;
  const dz = at.z - listener.z;
  const distance = Math.hypot(dx, dy, dz);
  if (distance < 1e-4) return { pan: 0, gain: 1 };
  const pan = (dx * right.x + dy * right.y + dz * right.z) / distance;
  return { pan: Math.min(1, Math.max(-1, pan)), gain: HALF_VOLUME_M / (HALF_VOLUME_M + distance) };
}

export function createAudio(): Audio {
  let ctx: AudioContext | undefined;
  let master: GainNode | undefined;
  let noise: AudioBuffer | undefined;
  let muted = false;
  let volume = DEFAULT_VOLUME;

  /** Pushes the current volume (or silence) at the master gain, ramped. */
  function applyGain(): void {
    if (master === undefined || ctx === undefined) return;
    // Ramped, never switched: cutting a gain mid-ring clicks.
    master.gain.linearRampToValueAtTime(muted ? 0 : volume, ctx.currentTime + 0.05);
  }

  /**
   * One second of white noise, made once and reused. Every airy sound here is a
   * window onto this buffer through a different filter, which is far cheaper than
   * generating noise per event and sounds no different.
   */
  function noiseBuffer(context: AudioContext): AudioBuffer {
    const buffer = context.createBuffer(1, context.sampleRate, context.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) samples[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /** The graph, or undefined if audio has not been unlocked yet. */
  function live(): { context: AudioContext; out: GainNode; noise: AudioBuffer } | undefined {
    if (ctx === undefined || master === undefined || noise === undefined) return undefined;
    if (muted) return undefined;
    // A tab switch suspends the context; nothing plays until it is running again.
    if (ctx.state === 'suspended') void ctx.resume();
    return { context: ctx, out: master, noise };
  }

  /**
   * Routes a source through a stereo pan and a per-sound gain into the master.
   * Returns the node a caller should connect their source to.
   */
  function channel(
    context: AudioContext,
    out: GainNode,
    at: Placement,
    level: number,
  ): AudioNode {
    const panner = context.createStereoPanner();
    panner.pan.value = at.pan;
    const gain = context.createGain();
    gain.gain.value = level * at.gain;
    gain.connect(panner);
    panner.connect(out);
    return gain;
  }

  /** A burst of filtered noise: the body of every airy sound here. */
  function burst(
    context: AudioContext,
    buffer: AudioBuffer,
    destination: AudioNode,
    options: {
      start: number;
      duration: number;
      type: BiquadFilterType;
      /** Filter frequency at the start, peak and end of the burst. */
      from: number;
      peak: number;
      to: number;
      q: number;
      attack: number;
      /** Peak level, 0..1. Defaults to full; used to tuck a layer under another. */
      level?: number;
    },
  ): void {
    const source = context.createBufferSource();
    source.buffer = buffer;
    // A random offset into the noise, so repeated swings are not identical.
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = options.type;
    filter.Q.value = options.q;
    const { start, duration } = options;
    filter.frequency.setValueAtTime(options.from, start);
    filter.frequency.linearRampToValueAtTime(options.peak, start + duration * 0.45);
    filter.frequency.linearRampToValueAtTime(options.to, start + duration);

    const env = context.createGain();
    // An exponential ramp cannot start from zero, so the attack is linear and only
    // the decay is exponential.
    const peak = Math.max(1e-4, options.level ?? 1);
    env.gain.setValueAtTime(0, start);
    env.gain.linearRampToValueAtTime(peak, start + options.attack);
    // Exponential to a floor rather than to zero: a ramp to exact zero is a
    // discontinuity, and it clicks. The floor stays below the peak, or a quiet
    // layer would ramp UPWARDS into it.
    env.gain.exponentialRampToValueAtTime(Math.min(0.0008, peak * 0.01), start + duration);

    source.connect(filter);
    filter.connect(env);
    env.connect(destination);
    source.start(start, Math.random() * (buffer.duration - duration - 0.01));
    source.stop(start + duration + 0.02);
  }

  /**
   * A struck-metal partial. Steel is inharmonic — that is why a sword sounds like
   * a sword and not like a tuning fork — so callers stack these at ratios that are
   * deliberately not whole numbers.
   */
  function partial(
    context: AudioContext,
    destination: AudioNode,
    options: { start: number; freq: number; decay: number; level: number },
  ): void {
    const osc = context.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(options.freq, options.start);
    // Metal drops slightly in pitch as it rings down.
    osc.frequency.exponentialRampToValueAtTime(options.freq * 0.94, options.start + options.decay);

    const env = context.createGain();
    env.gain.setValueAtTime(options.level, options.start);
    env.gain.exponentialRampToValueAtTime(0.0008, options.start + options.decay);

    osc.connect(env);
    env.connect(destination);
    osc.start(options.start);
    osc.stop(options.start + options.decay + 0.02);
  }

  return {
    unlock() {
      if (ctx !== undefined) return;
      // Constructed lazily and only from a gesture: a context created before one
      // starts suspended and every sound queued against it is silently lost.
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor === undefined) return;
      ctx = new Ctor();
      master = ctx.createGain();
      master.gain.value = muted ? 0 : volume;
      master.connect(ctx.destination);
      noise = noiseBuffer(ctx);
    },

    swing(clip, at) {
      const g = live();
      if (g === undefined) return;
      const { context, out, noise: buffer } = g;
      const now = context.currentTime;
      const to = channel(context, out, at, LEVEL.swing);

      // A stab is a shorter, tighter, higher hiss than a slash; a slash is a
      // broader sweep, because more blade is moving through more air.
      const stab = clip === 'stab';
      burst(context, buffer, to, {
        start: now,
        duration: stab ? 0.13 : 0.22,
        type: 'bandpass',
        from: stab ? 700 : 380,
        peak: stab ? 2400 : 1700,
        to: stab ? 900 : 520,
        q: stab ? 1.6 : 0.9,
        attack: 0.02,
      });
    },

    hit(speed, at) {
      const g = live();
      if (g === undefined) return;
      const { context, out, noise: buffer } = g;
      const now = context.currentTime;
      const p = power(speed);
      const to = channel(context, out, at, LEVEL.hit * (0.45 + 0.55 * p));

      // Wet, heavy and completely un-metallic. Built from three layers, and the
      // rule for all of them is NO sustained partials: a struck-metal tone is what
      // makes a sound read as steel, so the moment any of this rings, a landed hit
      // and a blocked one stop being distinguishable — which is the one job this
      // sound has.

      // 1. The body blow. Deep, short, dropping fast — the weight behind the cut.
      const thump = context.createOscillator();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(130, now);
      thump.frequency.exponentialRampToValueAtTime(42, now + 0.13);
      const thumpEnv = context.createGain();
      thumpEnv.gain.setValueAtTime(0.95 * p + 0.25, now);
      thumpEnv.gain.exponentialRampToValueAtTime(0.0008, now + 0.16);
      thump.connect(thumpEnv);
      thumpEnv.connect(to);
      thump.start(now);
      thump.stop(now + 0.18);

      // 2. The tear. A noise band whose centre frequency COLLAPSES downwards, which
      // is what reads as wet: pitch falling through a resonance is the sound of
      // something giving way, where a rising sweep would read as air.
      burst(context, buffer, to, {
        start: now,
        duration: 0.17,
        type: 'bandpass',
        from: 1500 + 600 * p,
        peak: 620,
        to: 170,
        q: 2.4,
        attack: 0.003,
      });

      // 3. The spatter, a beat later. Brief, unpitched, and quiet enough to be
      // texture rather than an event of its own — the spray after the impact rather
      // than a second hit. Scaled hard by power, so only real blows spray.
      burst(context, buffer, to, {
        start: now + 0.03,
        duration: 0.11,
        type: 'bandpass',
        from: 900,
        peak: 1500,
        to: 500,
        q: 1.2,
        attack: 0.008,
        level: 0.3 * p,
      });
    },

    block(speed, at) {
      const g = live();
      if (g === undefined) return;
      const { context, out, noise: buffer } = g;
      const now = context.currentTime;
      const p = power(speed);
      const to = channel(context, out, at, LEVEL.block * (0.4 + 0.6 * p));

      // Steel on steel: a bright transient over partials at deliberately
      // non-whole-number ratios. That inharmonicity is the whole thing — whole
      // ratios are a tuning fork, and a sword is not tuned. Harder contact rings
      // longer as well as louder.
      const decay = 0.16 + 0.22 * p;
      burst(context, buffer, to, {
        start: now,
        duration: 0.045,
        type: 'highpass',
        from: 2200,
        peak: 3800,
        to: 2600,
        q: 0.8,
        attack: 0.002,
      });
      for (const [ratio, level] of [
        [1, 0.5],
        [1.47, 0.32],
        [2.09, 0.2],
        // A high, short partial so the clang cuts through the rest of the mix.
        [3.16, 0.1],
      ] as const) {
        partial(context, to, {
          start: now,
          freq: 1750 * ratio,
          decay: decay / ratio,
          level: level * (0.5 + 0.5 * p),
        });
      }
    },

    parry(speed, at) {
      const g = live();
      if (g === undefined) return;
      const { context, out, noise: buffer } = g;
      const now = context.currentTime;
      const p = power(speed);
      const to = channel(context, out, at, LEVEL.parry * (0.5 + 0.5 * p));

      // Deliberately not a louder block. It is higher and it rings much longer,
      // because the player needs to know they got the timing right before they can
      // possibly have read the screen — this sound IS the feedback for the riposte
      // window opening.
      burst(context, buffer, to, {
        start: now,
        duration: 0.05,
        type: 'highpass',
        from: 3000,
        peak: 5200,
        to: 3400,
        q: 0.9,
        attack: 0.001,
      });
      const decay = 0.5 + 0.35 * p;
      for (const [ratio, level] of [
        [1, 0.42],
        [1.51, 0.3],
        [2.63, 0.22],
        [3.44, 0.12],
      ] as const) {
        partial(context, to, {
          start: now,
          freq: 2450 * ratio,
          decay: decay / Math.sqrt(ratio),
          level,
        });
      }
    },

    guard(up, at) {
      const g = live();
      if (g === undefined) return;
      const { context, out, noise: buffer } = g;
      const now = context.currentTime;
      const to = channel(context, out, at, LEVEL.guard);

      // Leather and weight shifting. Quiet on purpose: blocking is held for whole
      // seconds at a time and anything with presence here would grate within a
      // minute of play. Dropping the guard is duller than raising it.
      burst(context, buffer, to, {
        start: now,
        duration: up ? 0.07 : 0.055,
        type: 'bandpass',
        from: up ? 500 : 380,
        peak: up ? 1200 : 700,
        to: up ? 600 : 300,
        q: 1.1,
        attack: 0.006,
      });
    },

    setVolume(next) {
      volume = Math.min(1, Math.max(0, next));
      applyGain();
    },

    get volume() {
      return volume;
    },

    setMuted(next) {
      muted = next;
      applyGain();
    },

    get muted() {
      return muted;
    },

    dispose() {
      void ctx?.close();
      ctx = undefined;
      master = undefined;
      noise = undefined;
    },
  };
}
